import api, { apiWithoutInterceptor } from "./api";
import TokenService from "./token.service";
import type { LoginResponse, RegisterRequest } from "../types";

class AuthService {
  // Login Request - First step
  loginRequest(email: string, password: string) {
    return api().post("/Auth/LoginRequest", { email, password });
  }

  // Login - Second step with authenticator code
  login(
    email: string,
    password: string,
    authenticatorCode: string
  ): Promise<LoginResponse> {
    return api()
      .post("/Auth/Login", {
        email,
        password,
        authenticatorCode,
      })
      .then((response) => {
        if (response.data.accessToken) {
          TokenService.setUser(response.data);
        }
        return response.data;
      })
      .catch((err) => {
        console.error("Login error:", err);
        throw err;
      });
  }

  // Register Start - First step
  registerStart(email: string, password: string) {
    return api().post("/Auth/RegisterStart", { email, password });
  }

  // Register Verify - Second step with verification code
  registerVerify(email: string, verificationCode: string) {
    return api().post("/Auth/RegisterVerify", { email, verificationCode });
  }

  // Register - Final step
  register(registerData: RegisterRequest): Promise<LoginResponse> {
    return api()
      .post("/Auth/Register", registerData)
      .then((response) => {
        if (response.data.accessToken) {
          TokenService.setUser(response.data);
        }
        return response.data;
      });
  }

  // Refresh Token - Interceptor'suz instance kullan
  // Bu sayede 401 aldığında sonsuz döngüye girmez
  refreshToken(refreshToken: string): Promise<LoginResponse> {
    return apiWithoutInterceptor
      .post("/Auth/RefreshToken", { refreshToken })
      .then((response) => {
        if (response.data.accessToken) {
          TokenService.setUser(response.data);
        }
        return response.data;
      });
  }

  // Revoke Token
  revokeToken(refreshToken: string) {
    return api().post("/Auth/RevokeToken", { refreshToken });
  }

  logout() {
    TokenService.removeUser();
  }

  // Manager Registration (Public - No token required)
  registerManager(registrationData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    passwordConfirm: string;
  }) {
    // ASP.NET otomatik camelCase -> PascalCase map eder
    return api().post("/Auth/register-manager", registrationData);
  }

  // ============================================
  // New Onboarding Flow
  // ============================================

  /**
   * SMS doğrulama kodu gönder
   */
  sendVerificationCode(phone: string) {
    return api().post("/Auth/send-verification-code", { phone });
  }

  /**
   * SMS kodunu doğrula
   */
  verifyCode(phone: string, code: string) {
    return api().post("/Auth/verify-code", { phone, code });
  }

  /**
   * Kayıt öncesi validasyon kontrolleri
   * Email, telefon, şirket adı benzersiz mi kontrol eder
   */
  checkAvailability(data: {
    email?: string;
    phone?: string;
    companyName?: string;
  }) {
    return api().post<{
      success: boolean;
      emailAvailable?: boolean;
      phoneAvailable?: boolean;
      companyNameAvailable?: boolean;
      errors?: {
        email?: string;
        phone?: string;
        companyName?: string;
      };
    }>("/Auth/check-availability", data);
  }

  /**
   * Kayıt tamamla (ödeme sonrası)
   */
  completeRegistration(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    verificationToken: string;
    paymentMerchantOid: string;
    packageId: string;
    months: number;
    verificationCode?: string;
    companyName?: string;
    userAddress?: string;
    restaurantName?: string;
    restaurantAddress?: string;
  }) {
    return api().post("/Auth/complete-registration", data);
  }
}

export default new AuthService();
